import React from 'react'
import { LinkProps as ReactRouterLinkProps } from 'react-router-dom'

// Styles
import { InternalLink, ExternalLink, Variants } from './Link.styles'

interface CommonProps {
  variant?: Variants
  isUnderlined?: boolean
}

type InternalLinkProps = ReactRouterLinkProps &
  CommonProps & { external?: false }

type ExternalLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> &
  CommonProps & { external: true; to?: never }

type LinkProps = InternalLinkProps | ExternalLinkProps

/**
 * A Link component that renders a Prism styled 'react-router-dom' Link.
 *
 * This component extends 'react-router-dom' Link component.
 *
 * Variants:
 *
 *  - 'primary' -> Beige link
 *  - 'secondary' -> Black link
 *  - 'tertiary' -> Grey link
 *
 * Examples:
 *
 * Internal Link
 *
 * ```jsx
 * <Link
 *    to={...}
 *    variant="secondary"
 *    isUnderlined
 * />
 * ```
 *
 * External Link
 *
 * ```jsx
 * <Link
 *    href={...}
 *    variant="primary"
 *    isUnderlined
 *    external
 * />
 * ```
 */
const Link: React.FC<LinkProps> = ({
  variant = 'primary',
  isUnderlined = false,
  external,
  href,
  target,
  rel,
  children,
  className,
  onClick,
  ...props
}) => {
  if (external) {
    return (
      <ExternalLink
        $variant={variant}
        $isUnderlined={isUnderlined}
        href={href}
        target={target}
        rel={rel}
        className={className}
        onClick={onClick}>
        {children}
      </ExternalLink>
    )
  }

  return (
    <InternalLink
      $variant={variant}
      $isUnderlined={isUnderlined}
      className={className}
      onClick={onClick}
      {...(props as ReactRouterLinkProps)}>
      {children}
    </InternalLink>
  )
}

export default Link
